import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { MarketingCampaignState, ScheduledCampaign, UpdateCampaignPostPayload, DeleteCampaignPostPayload } from '../../components/marketingCampaign/types';

const initialState: MarketingCampaignState = {
  campaigns: [],
  loading: false,
  error: null,
  selectedCampaign: null,
  updating: false,
  updateError: null,
  deleting: false,
  deleteError: null,
};

const marketingCampaignSlice = createSlice({
  name: 'marketingCampaign',
  initialState,
  reducers: {
    fetchMarketingCampaignsRequest: (state, action: PayloadAction<string>) => {
      state.loading = true;
      state.error = null;
    },
    fetchMarketingCampaignsSuccess: (state, action: PayloadAction<ScheduledCampaign[]>) => {
      state.loading = false;
      state.campaigns = action.payload;
    },
    fetchMarketingCampaignsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setSelectedCampaign: (state, action: PayloadAction<ScheduledCampaign | null>) => {
      state.selectedCampaign = action.payload;
    },
    clearMarketingCampaigns: (state) => {
      state.campaigns = [];
      state.error = null;
      state.selectedCampaign = null;
    },
    updateCampaignPostRequest: (state, action: PayloadAction<UpdateCampaignPostPayload>) => {
      state.updating = true;
      state.updateError = null;
    },
    updateCampaignPostSuccess: (state) => {
      state.updating = false;
      state.updateError = null;
    },
    updateCampaignPostFailure: (state, action: PayloadAction<string>) => {
      state.updating = false;
      state.updateError = action.payload;
    },
    deleteCampaignPostRequest: (state, action: PayloadAction<DeleteCampaignPostPayload>) => {
      state.deleting = true;
      state.deleteError = null;
      // Optimistic update: remove campaign from state immediately
      state.campaigns = state.campaigns.filter(
        (campaign: any) => campaign.id !== action.payload.campaignId
      );
    },
    deleteCampaignPostSuccess: (state) => {
      state.deleting = false;
      state.deleteError = null;
    },
    deleteCampaignPostFailure: (state, action: PayloadAction<string>) => {
      state.deleting = false;
      state.deleteError = action.payload;
      // On failure, refetch campaigns to restore the deleted one
    },
    updateSingleCampaign: (state, action: PayloadAction<ScheduledCampaign>) => {
      // Find and update a single campaign in the list
      const index = state.campaigns.findIndex((c: any) => c.id === action.payload.id);
      if (index !== -1) {
        state.campaigns[index] = action.payload;
      }
    },
  },
});

export const {
  fetchMarketingCampaignsRequest,
  fetchMarketingCampaignsSuccess,
  fetchMarketingCampaignsFailure,
  setSelectedCampaign,
  clearMarketingCampaigns,
  updateCampaignPostRequest,
  updateCampaignPostSuccess,
  updateCampaignPostFailure,
  deleteCampaignPostRequest,
  deleteCampaignPostSuccess,
  deleteCampaignPostFailure,
  updateSingleCampaign,
} = marketingCampaignSlice.actions;

export default marketingCampaignSlice.reducer;
